import React, { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { formatMoney } from '@dill/dill-shared/dist/src';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillButton } from '../../../general/Dill/DillButton';
import emptyListImg from '../../../../assets/images/emptyList.png';

type PlaidSummary = {
  bankName: string;
  date: string;
  amount: number;
  numberOfTransactions: number;
  transactions: {
    name?: string | null;
    description?: string | null;
    date: string;
    amount: number;
    detailedCategory?: string | null;
  }[];
};

const PlaidSummaryModal = ({
  open,
  handleClose,
  summary,
}: {
  open: boolean;
  handleClose: () => void;
  summary: PlaidSummary;
}) => {
  const columns = useMemo(
    () => [
      { id: 'name', name: 'Name', width: 150 },
      { id: 'description', name: 'Description', width: 200 },
      { id: 'date', name: 'Date', width: 100 },
      { id: 'amount', name: 'Amount', width: 150 },
    ],
    []
  );

  const totalWidth = useMemo(() => columns.reduce((acc, col) => acc + col.width, 0), [columns]);

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Summary"
      subtitle={summary.bankName}
      maxWidth="70vw"
      contentClassName="flex-1 min-h-0 overflow-y-auto px-5 py-4 flex flex-col gap-3"
      footer={<DillButton label="Close" variant="secondary" onClick={handleClose} />}>
      <div className="text-sm text-gray-600">
        <span className="font-medium">Month:</span> {summary.date}
      </div>
      <div className="text-sm text-gray-600">
        <span className="font-medium">Cumulative Amount:</span> {formatMoney(summary.amount, 2)}
      </div>

      <div className="w-full border rounded-lg overflow-hidden">
        <div className="w-full flex border-b justify-between px-4 sticky top-0 PRIMARY_50-BG items-center z-10">
          {columns.map((column) => (
            <div
              key={column.id}
              style={{ flex: column.width / totalWidth }}
              className={twMerge('flex py-3 mx-1 items-center')}>
              <p className="text-xs TEXT_SECONDARY-CLR">{column.name}</p>
            </div>
          ))}
        </div>

        {summary.transactions.length === 0 ? (
          <div className="w-full py-12 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <img className="w-32 object-contain" src={emptyListImg} alt="" />
              <p className="text-sm text-gray-400">No transactions</p>
            </div>
          </div>
        ) : (
          summary.transactions.map((transaction, index) => (
            <div
              key={index}
              className="w-full flex items-center px-4 justify-between border-b last:border-b-0">
              {columns.map((column) => (
                <div
                  key={column.id}
                  style={{ flex: column.width / totalWidth }}
                  className="flex py-3 mx-1">
                  {column.id === 'name' && (
                    <p className="text-xs capitalize">{transaction.name || ''}</p>
                  )}
                  {column.id === 'description' && (
                    <p className="text-xs capitalize">{transaction.description || ''}</p>
                  )}
                  {column.id === 'date' && <p className="text-xs">{transaction.date ?? ''}</p>}
                  {column.id === 'amount' && (
                    <p className="text-xs">{formatMoney(transaction.amount ?? 0, 2)}</p>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </DillModal>
  );
};

export default PlaidSummaryModal;
